import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getMyCvEntitlement } from "@/lib/api/cv-billing.functions";
import {
  Sparkles,
  Download,
  Loader2,
  Crown,
  Upload,
  FileUp,
  Linkedin,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { CV_TEMPLATES, type CVData } from "@/lib/cv-templates";
import { loadTemplateId, saveTemplateId } from "@/lib/cv-template-preference";
import { fetchWithAuth } from "@/lib/fetch-with-auth";
import { normaliseSaLocation } from "@/lib/sa-locations";

import { CVPreview, MiniPreview, accentHexFor } from "@/components/cv/CVPreview";
import { generateCvPdf } from "@/lib/api/cv.functions";
import { deliverDocsToClientAccount } from "@/lib/api/client-delivery.functions";
import { computeAtsReport, type AtsReport } from "@/lib/cv-ats";
import { KeywordChecker } from "@/components/cv/KeywordChecker";
import { toast } from "sonner";
import { MotivationalLetterGenerator } from "@/components/cv/MotivationalLetterGenerator";

const EXAMPLE = `Name: Thabo Mokoena
Email: thabo@example.com  |  Phone: 071 234 5678  |  Pretoria, Gauteng
LinkedIn: linkedin.com/in/thabo

About me: Compliance officer with 4 years' experience in financial services. Strong with POPIA, FICA, and AML controls.

Experience:
- Compliance Officer at ABC Bank (2022-Present): rolled out POPIA framework, trained 120 staff, reduced audit findings by 40%.
- Junior Compliance Analyst at XYZ Insurance (2020-2022): supported FICA risk reviews, drafted SOPs.

Education:
- BCom Law, University of Pretoria, 2019

Skills: POPIA, FICA, risk assessment, policy drafting, training, MS 365.
Languages: English, Sesotho, isiZulu.`;

export interface CVBuilderProps {
  /** Admin/internal mode — strips the brand footer from the rendered PDF. */
  unbranded?: boolean;
  /** Optional preset template id (from search params). */
  presetTemplate?: string;
  /** Optional heading override. */
  title?: string;
  /** Optional subhead override (React node). */
  subhead?: React.ReactNode;
  /** Public, no-auth mode (free CV builder on marketing site). Cap is enforced server-side. */
  publicMode?: boolean;
  /** Display cap when publicMode is true (default 2). Server is authoritative — never increase past server FREE_LIMIT. */
  freeLimit?: number;
}


const FREE_QUOTA_KEY = "ndlcs:free-cv-gens:v1";
const VISITOR_ID_KEY = "ndlcs:free-cv-visitor:v1";
const MIN_ATS_TO_DOWNLOAD = 50;

function readFreeUsage(): number {
  if (typeof window === "undefined") return 0;
  const raw = window.localStorage.getItem(FREE_QUOTA_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function writeFreeUsage(n: number) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(FREE_QUOTA_KEY, String(Math.max(0, n)));
}
function getOrCreateVisitorId(): string {
  if (typeof window === "undefined") return "";
  let v = window.localStorage.getItem(VISITOR_ID_KEY);
  if (!v) {
    v =
      crypto.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(VISITOR_ID_KEY, v);
  }
  return v;
}

export function CVBuilder({ unbranded, presetTemplate, title, subhead, publicMode, freeLimit = 2 }: CVBuilderProps) {
  const [freeUsed, setFreeUsed] = useState<number>(() => (publicMode ? readFreeUsage() : 0));
  const freeRemaining = Math.max(0, freeLimit - freeUsed);
  const quotaExhausted = publicMode && freeRemaining <= 0;

  // Authenticated entitlement (free + paid CV credits). Skipped in publicMode.
  const getEnt = useServerFn(getMyCvEntitlement);
  const entQ = useQuery({
    queryKey: ["cv-entitlement"],
    queryFn: () => getEnt(),
    enabled: !publicMode,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
  const authedUnlimited = Boolean(entQ.data && (entQ.data as any).unlimited);
  const authedCreditsRemaining = entQ.data
    ? entQ.data.freeRemaining + entQ.data.paidRemaining
    : 0;
  const authedNoCredits =
    !publicMode && entQ.data && !authedUnlimited
      ? authedCreditsRemaining <= 0
      : false;
  const [templateId, setTemplateId] = useState<string>(
    presetTemplate || CV_TEMPLATES[0].id,
  );
  // Remember the chosen template so the ATS scanner exports the revamped CV
  // in exactly the same design.
  useEffect(() => {
    if (!presetTemplate) setTemplateId(loadTemplateId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    saveTemplateId(templateId);
  }, [templateId]);
  const [tierFilter, setTierFilter] = useState<"all" | "free" | "15" | "30">("all");
  const [targetRole, setTargetRole] = useState("");
  const [roleSuggestions, setRoleSuggestions] = useState<string[]>([]);
  const [scopeSuggestions, setScopeSuggestions] = useState<
    { role?: string; text: string; basis?: string }[]
  >([]);
  const [acceptedScope, setAcceptedScope] = useState<Record<number, boolean>>({});


  const [tone, setTone] = useState("professional");
  const [inputs, setInputs] = useState("");
  const [cv, setCv] = useState<CVData | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [importText, setImportText] = useState("");
  const [importSource, setImportSource] = useState<"paste" | "upload" | "linkedin">("paste");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [fileMeta, setFileMeta] = useState<{ name: string; size: number } | null>(null);

  // Tailor-to-a-specific-job (optional): job ad photo + pasted description
  const jobFileRef = useRef<HTMLInputElement | null>(null);
  const [jobText, setJobText] = useState("");
  const [jobPhotoName, setJobPhotoName] = useState<string | null>(null);

  // Structured quick-details (optional helper to make the "easy and accurate" path)
  const [qd, setQd] = useState({
    fullName: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    yearsExp: "",
  });
  function applyQuickDetails() {
    const header: string[] = [];
    if (qd.fullName) header.push(`Name: ${qd.fullName}`);
    const contact = [qd.email, qd.phone, qd.location].filter(Boolean).join("  |  ");
    if (contact) header.push(contact);
    if (qd.linkedin) header.push(`LinkedIn: ${qd.linkedin}`);
    if (qd.yearsExp) header.push(`Years of experience: ${qd.yearsExp}`);
    const block = header.join("\n");
    if (!block) return;
    setInputs((prev) => (prev ? `${block}\n\n${prev}` : `${block}\n\nAbout me: \n\nExperience:\n- \n\nEducation:\n- \n\nSkills: \nLanguages: `));
  }

  /**
   * Guarantees a detected LinkedIn (and other profile links) survive AI
   * generation and appear in the CV header, preview and every export.
   */
  function withDetectedLinks(data: CVData): CVData {
    const found: { label: string; url: string }[] = [...(data.links ?? [])];
    const add = (rawUrl: string, label: string) => {
      let url = rawUrl.trim().replace(/[),.;]+$/, "");
      if (!url) return;
      if (/^in\//i.test(url)) url = `linkedin.com/${url}`;
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
      const key = url.toLowerCase().replace(/\/$/, "");
      if (found.some((l) => l.url.toLowerCase().replace(/\/$/, "") === key)) return;
      found.push({ label, url });
    };
    if (qd.linkedin.trim()) add(qd.linkedin, "LinkedIn");
    for (const m of inputs.matchAll(
      /(?:https?:\/\/)?(?:[\w-]+\.)?linkedin\.com\/[^\s|,;)]+/gi,
    ))
      add(m[0], "LinkedIn");
    return found.length ? { ...data, links: found } : data;
  }

  /** Append the scope points the candidate confirmed as true to the brief. */
  function addAcceptedScopeToInputs() {
    const picked = scopeSuggestions.filter((_, i) => acceptedScope[i]);
    if (!picked.length) return;
    const block = [
      "Confirmed scope of my roles (true and verifiable):",
      ...picked.map((s) => `- ${s.role ? `${s.role}: ` : ""}${s.text}`),
    ].join("\n");
    setInputs((prev) => (prev ? `${prev}\n\n${block}` : block));
    setAcceptedScope({});
    toast.success("Added to your background details.");
  }


  // Captcha state — only used in publicMode
  const [captcha, setCaptcha] = useState<{ question: string; token: string } | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [visitorId, setVisitorId] = useState("");

  useEffect(() => {
    if (!publicMode) return;
    setVisitorId(getOrCreateVisitorId());
    void refreshCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicMode]);

  async function refreshCaptcha() {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    try {
      const res = await fetch("/api/public/cv-generate", { method: "GET" });
      if (res.ok) {
        const c = (await res.json()) as { question: string; token: string };
        setCaptcha(c);
      }
    } finally {
      setCaptchaLoading(false);
    }
  }

  const template = useMemo(
    () => CV_TEMPLATES.find((t) => t.id === templateId) ?? CV_TEMPLATES[0],
    [templateId],
  );

  const extract = useMutation({
    mutationFn: async () => {
      let filePayload: { name: string; mime: string; data: string } | undefined;
      const f = fileRef.current?.files?.[0];
      if (f) {
        if (f.size > 8 * 1024 * 1024) throw new Error("File too large (max 8MB).");
        const buf = new Uint8Array(await f.arrayBuffer());
        let bin = "";
        for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
        filePayload = { name: f.name, mime: f.type || "application/pdf", data: btoa(bin) };
      }
      const raw = importText.trim();
      // A LinkedIn profile URL on its own can't be read — LinkedIn blocks
      // automated profile fetching. Capture it as a contact detail and tell
      // the user exactly what to paste instead.
      const urlOnly = /^(https?:\/\/)?(www\.)?linkedin\.com\/[^\s]*$/i.test(raw);
      if (urlOnly) {
        const clean = raw.startsWith("http") ? raw : `https://${raw}`;
        setQd((p) => ({ ...p, linkedin: clean }));
        throw new Error(
          "We saved that LinkedIn URL to your contact details, but LinkedIn blocks automated reading of profiles. On LinkedIn open your profile → More → Save to PDF and upload that file, or copy your About, Experience and Education text and paste it here.",
        );
      }
      if (!filePayload && !raw) {
        throw new Error("Upload a file or paste your CV / LinkedIn text first.");
      }

      const res = await fetchWithAuth("/api/cv-extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: importText, file: filePayload, source: importSource }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok) throw new Error(data?.error || `Failed to extract (${res.status})`);
      return data.extracted as {
        extracted_text?: string;
        summary?: string;
        fullName?: string;
        email?: string;
        phone?: string;
        location?: string;
        linkedin?: string;
        portfolio?: string;
        headline?: string;
        skills?: string[];
        experience?: any[];
        education?: any[];
        certifications?: any[];
        languages?: string[];
        suggestedRoles?: string[];
        scopeSuggestions?: { role?: string; text: string; basis?: string }[];
      };
    },
    onSuccess: (ex) => {
      // --- Auto-fill the contact block so nothing is typed twice ----------
      const detectedLocation = normaliseSaLocation(ex.location || "");
      setQd((p) => ({
        ...p,
        fullName: ex.fullName?.trim() || p.fullName,
        email: ex.email?.trim() || p.email,
        phone: ex.phone?.trim() || p.phone,
        location: detectedLocation || p.location,
        linkedin: ex.linkedin?.trim() || p.linkedin,
      }));

      // --- Suggested target roles ----------------------------------------
      const roles = (ex.suggestedRoles || []).filter(Boolean);
      setRoleSuggestions(roles);
      if (roles.length) {
        setTargetRole((prev) => prev.trim() || roles[0]);
      } else if (ex.headline?.trim()) {
        setTargetRole((prev) => prev.trim() || ex.headline!.trim());
      }

      // --- Evidence-based scope prompts (opt-in, never auto-claimed) ------
      setScopeSuggestions(
        (ex.scopeSuggestions || []).filter((s) => s && s.text?.trim()),
      );
      setAcceptedScope({});

      const lines: string[] = [];
      if (ex.fullName) lines.push(`Name: ${ex.fullName}`);
      const contact = [ex.email, ex.phone, detectedLocation, ex.linkedin, ex.portfolio]
        .filter(Boolean)
        .join("  |  ");
      if (contact) lines.push(contact);
      if (ex.summary) lines.push(`\nAbout me: ${ex.summary}`);
      if (ex.experience?.length) {
        lines.push(`\nExperience:`);
        ex.experience.forEach((e: any) => {
          lines.push(
            `- ${e.role || ""} at ${e.company || ""}${e.location ? `, ${e.location}` : ""} (${e.start || ""}-${e.end || "Present"})`,
          );
          (e.bullets || []).forEach((b: string) => lines.push(`  • ${b}`));
        });
      }
      if (ex.education?.length) {
        lines.push(`\nEducation:`);
        ex.education.forEach((e: any) =>
          lines.push(`- ${e.qualification || ""}, ${e.institution || ""}${e.year ? `, ${e.year}` : ""}${e.notes ? ` — ${e.notes}` : ""}`),
        );
      }
      if (ex.certifications?.length) {
        lines.push(`\nCertifications:`);
        ex.certifications.forEach((c: any) =>
          lines.push(`- ${c.name || ""}${c.issuer ? `, ${c.issuer}` : ""}${c.year ? ` (${c.year})` : ""}`),
        );
      }
      if (ex.skills?.length) lines.push(`\nSkills: ${ex.skills.join(", ")}`);
      if (ex.languages?.length) lines.push(`Languages: ${ex.languages.join(", ")}`);
      setInputs(lines.join("\n"));
    },
  });


  const gen = useMutation({
    mutationFn: async () => {
      if (publicMode) {
        if (!captcha || !captchaAnswer.trim()) {
          throw new Error("Please solve the captcha first.");
        }
        const res = await fetch("/api/public/cv-generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetRole,
            tone,
            inputs,
            visitorId,
            captchaToken: captcha.token,
            captchaAnswer: Number(captchaAnswer),
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (data?.needsNewCaptcha) await refreshCaptcha();
          throw new Error(data?.error || "Failed to generate");
        }
        if (data.nextCaptcha) {
          setCaptcha(data.nextCaptcha);
          setCaptchaAnswer("");
        }
        if (data.quota?.used != null) {
          writeFreeUsage(data.quota.used);
          setFreeUsed(data.quota.used);
        }
        return data.cv as CVData;
      }
      let jobImagePayload: { mime: string; data: string } | null = null;
      const jf = jobFileRef.current?.files?.[0];
      if (jf) {
        if (!jf.type.startsWith("image/")) throw new Error("Job ad must be an image.");
        if (jf.size > 8 * 1024 * 1024) throw new Error("Job ad image too large (max 8MB).");
        const buf = new Uint8Array(await jf.arrayBuffer());
        let bin = "";
        for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
        jobImagePayload = { mime: jf.type, data: btoa(bin) };
      }
      const res = await fetchWithAuth("/api/cv-generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          targetRole,
          tone,
          inputs,
          jobText: jobText || undefined,
          jobImage: jobImagePayload,
        }),
      });
      const data = await res.json().catch(() => ({}) as any);
      if (!res.ok) throw new Error(data?.error || `Failed to generate (${res.status})`);
      return data.cv as CVData;
    },
    onSuccess: (data) => {
      setCv(withDetectedLinks(data));
      toast.success("CV generated — scroll down to preview & download.");
      if (!publicMode) entQ.refetch();
      setTimeout(() => {
        previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    },
    onError: () => {
      if (!publicMode) entQ.refetch();
    },
  });


  const generatePdfFn = useServerFn(generateCvPdf);
  const deliverToAccount = useServerFn(deliverDocsToClientAccount);
  const pdfDownload = useMutation({
    mutationFn: async () => {
      if (!cv) throw new Error("Generate the CV first.");
      if (publicMode && atsReport && atsReport.score < MIN_ATS_TO_DOWNLOAD) {
        throw new Error(
          `Your ATS score is ${atsReport.score}/100 — strengthen the highlighted items below (minimum ${MIN_ATS_TO_DOWNLOAD}) before downloading.`,
        );
      }
      const filename = `${(cv.fullName || "CV").replace(/\s+/g, "_")}_CV.pdf`;

      let blob: Blob;
      let downloadName = filename;
      let base64: string | null = null;
      if (publicMode) {
        // Public, no-auth PDF endpoint — server forces unbranded output.
        const res = await fetch("/api/public/cv-pdf", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            cv,
            accentHex: accentHexFor(template),
            filename,
          }),
        });
        if (!res.ok) throw new Error(await res.text().catch(() => "Failed to render PDF"));
        blob = await res.blob();
      } else {
        const res = await generatePdfFn({
          data: {
            cv,
            accentHex: accentHexFor(template),
            filename,
            unbranded: unbranded ? true : undefined,
          },
        });
        base64 = res.base64;
        const bin = atob(res.base64);
        const u8 = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
        blob = new Blob([u8], { type: res.contentType });
        downloadName = res.filename;
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Saved "${downloadName}" to your Downloads folder.`);

      // Admin mode: if the CV carries a client email that matches a portal
      // account, drop the finished PDF straight into that client's files.
      const clientEmail = (cv.email || "").trim().toLowerCase();
      if (unbranded && base64 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail)) {
        try {
          const out: any = await deliverToAccount({
            data: {
              email: clientEmail,
              clientName: cv.fullName || undefined,
              source: "cv_builder",
              files: [
                {
                  filename: downloadName,
                  contentBase64: base64,
                  contentType: "application/pdf",
                  label: "CV prepared by NDLCS",
                },
              ],
            },
          });
          if (out?.delivered) {
            toast.success(`Also sent to ${clientEmail}'s portal account.`);
          } else {
            toast.message(`No portal account found for ${clientEmail} — not delivered.`);
          }
        } catch {
          /* delivery is best-effort; the download already succeeded */
        }
      }
      return true;
    },

    onError: (e: any) => {
      toast.error(e?.message || "Couldn't download PDF.");
    },
  });

  const saSuggestions = useMemo(() => computeSASuggestions(cv, inputs), [cv, inputs]);
  const atsReport: AtsReport | null = useMemo(
    () => (cv ? computeAtsReport(cv, targetRole) : null),
    [cv, targetRole],
  );
  const atsBlocksDownload =
    !!publicMode && !!atsReport && atsReport.score < MIN_ATS_TO_DOWNLOAD;

  function handlePrint() {
    window.print();
  }


  return (
    <div className="space-y-6">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #cv-print, #cv-print * { visibility: visible !important; }
          #cv-print { position: absolute; inset: 0; box-shadow: none !important; }
        }
      `}</style>

      <header>
        <h1 className="font-display text-2xl text-navy">{title ?? "AI CV Builder"}</h1>
        {subhead ?? (
          <p className="mt-1 text-sm text-foreground/70">
            Describe your background and target role. Our AI drafts a tailored,
            ATS-friendly CV in your chosen template. Need a human review or
            interview coaching?{" "}
            <Link to="/book" className="text-navy underline">
              Book a session
            </Link>
            .
          </p>
        )}
        {unbranded && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
            <ShieldDot /> Internal / unbranded mode — PDF will not carry the firm
            footer.
          </p>
        )}
      </header>

      <Stepper
        currentStep={cv ? 4 : inputs && targetRole ? 3 : templateId ? 2 : 1}
      />


      {publicMode && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm ${
            quotaExhausted
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-navy/30 bg-navy/[0.04] text-navy"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {quotaExhausted ? (
              <>You've used all {freeLimit} free AI generations on this browser.</>
            ) : (
              <>
                Free AI CV builder — <strong>{freeRemaining}</strong> of {freeLimit} generations
                left on this browser. Downloads are always free.
              </>
            )}
          </span>
          <Link
            to="/auth"
            className="rounded-md bg-navy px-3 py-1.5 text-xs font-medium text-navy-foreground hover:bg-navy/90"
          >
            Create free account
          </Link>
        </div>
      )}

      {publicMode && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-900">
          <ShieldCheck className="mr-1.5 inline h-3.5 w-3.5 align-text-top" />
          <strong>100% ad-free.</strong> No third-party trackers. PDFs export
          clean — no watermark, no branding, no promotional footer.
        </div>
      )}




      <section className="rounded-lg border border-border bg-card p-5 space-y-3">
        <h2 className="text-sm font-medium text-navy">
          1. Import an existing CV or LinkedIn (optional)
        </h2>
        <p className="text-xs text-foreground/70">
          Upload a PDF/Word CV or paste a LinkedIn profile export — we'll extract
          the content and pre-fill the AI builder. Nothing is stored on our
          server beyond this session.
        </p>
        <div className="flex flex-wrap gap-2 text-xs">
          {([
            ["upload", "Upload CV", FileUp],
            ["linkedin", "LinkedIn paste", Linkedin],
            ["paste", "Paste any text", Upload],
          ] as const).map(([k, label, Icon]) => (
            <button
              key={k}
              type="button"
              onClick={() => setImportSource(k)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 ${
                importSource === k ? "border-navy bg-navy/5 text-navy" : "border-input hover:border-navy"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {importSource === "upload" && (
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">
              CV file (PDF, DOCX, TXT, or image — max 8MB)
            </span>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFileMeta(f ? { name: f.name, size: f.size } : null);
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-navy file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-navy-foreground"
            />
            {fileMeta && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {fileMeta.name} · {(fileMeta.size / 1024).toFixed(0)} KB
              </p>
            )}
          </label>
        )}

        {importSource === "linkedin" && (
          <div className="space-y-3">
            <label className="block text-xs">
              <span className="mb-1 block font-medium text-foreground/80">
                LinkedIn profile URL (optional — added to your CV header)
              </span>
              <input
                type="url"
                inputMode="url"
                aria-label="LinkedIn profile URL"
                value={qd.linkedin}
                onChange={(e) => setQd({ ...qd, linkedin: e.target.value })}
                placeholder="https://www.linkedin.com/in/your-name"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                LinkedIn blocks apps from reading profiles from a link, so a URL
                on its own can't be imported. We'll add it to your contact
                details — then paste your profile text below or upload your
                “Save to PDF” export.
              </p>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-medium text-foreground/80">
                Paste LinkedIn profile text
              </span>
              <p className="mb-1 text-[11px] text-muted-foreground">
                On LinkedIn → your profile → More → Save to PDF (then use “Upload
                CV” above), or copy your About, Experience and Education
                sections and paste them below.
              </p>
              <textarea
                rows={6}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste LinkedIn About / Experience / Education sections here…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
            </label>
          </div>
        )}


        {importSource === "paste" && (
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">
              Paste any free-form text
            </span>
            <textarea
              rows={6}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste anything — a bio, an old CV, notes about a career…"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </label>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => extract.mutate()}
            disabled={extract.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-navy bg-background px-4 py-2 text-sm font-medium text-navy hover:bg-navy/5 disabled:opacity-60"
          >
            {extract.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Extract & pre-fill builder
          </button>
          {extract.error && (
            <p className="text-xs text-red-600">{(extract.error as Error).message}</p>
          )}
          {extract.isSuccess && !extract.isPending && (
            <p className="text-xs text-emerald-700">
              ✓ Extracted — contact details, location and target role filled in
              for you below.
            </p>
          )}
        </div>

        {extract.isSuccess && (
          <div className="space-y-3 rounded-md border border-emerald-600/25 bg-emerald-50/50 p-3">
            <div>
              <p className="text-xs font-semibold text-navy">
                Detected contact details
              </p>
              <ul className="mt-1 grid gap-x-4 gap-y-0.5 text-[11px] text-foreground/80 sm:grid-cols-2">
                {([
                  ["Name", qd.fullName],
                  ["Email", qd.email],
                  ["Phone", qd.phone],
                  ["Location", qd.location],
                  ["LinkedIn", qd.linkedin],
                ] as const).map(([label, value]) => (
                  <li key={label} className="truncate">
                    <span className="font-medium">{label}:</span>{" "}
                    {value ? (
                      value
                    ) : (
                      <span className="text-muted-foreground">not found — add it below</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-[11px] text-muted-foreground">
                South African towns are automatically completed with their
                province (e.g. Benoni → Benoni, Gauteng, South Africa).
              </p>
            </div>

            {roleSuggestions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-navy">
                  Suitable roles based on your experience
                </p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {roleSuggestions.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setTargetRole(r)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] ${
                        targetRole === r
                          ? "border-navy bg-navy text-navy-foreground"
                          : "border-navy/30 bg-background text-navy hover:bg-navy/5"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {scopeSuggestions.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-navy">
                  Scope of your roles — tick what is true for you
                </p>
                <p className="mb-1.5 text-[11px] text-muted-foreground">
                  These describe the real, everyday weight of the jobs you held.
                  Nothing is added to your CV unless you tick it, and we never
                  invent figures — where a number would help, confirm your own.
                </p>
                <ul className="space-y-1.5">
                  {scopeSuggestions.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px]">
                      <input
                        type="checkbox"
                        id={`scope-${i}`}
                        checked={!!acceptedScope[i]}
                        onChange={(e) =>
                          setAcceptedScope((p) => ({ ...p, [i]: e.target.checked }))
                        }
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-current text-navy"
                      />
                      <label htmlFor={`scope-${i}`} className="cursor-pointer">
                        <span className="text-foreground/90">{s.text}</span>
                        {(s.role || s.basis) && (
                          <span className="block text-muted-foreground">
                            {[s.role, s.basis].filter(Boolean).join(" — ")}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={addAcceptedScopeToInputs}
                  disabled={!Object.values(acceptedScope).some(Boolean)}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-navy bg-background px-3 py-1.5 text-[11px] font-medium text-navy hover:bg-navy/5 disabled:opacity-50"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Add ticked points to my background
                </button>
              </div>
            )}
          </div>
        )}
      </section>


      <section className="rounded-lg border border-border bg-card p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-navy">2. Choose a template & tier</h2>
          <span className="text-[11px] text-muted-foreground">
            {CV_TEMPLATES.length} templates · free + premium tiers
          </span>
        </div>

        {/* Tier filter */}
        <div className="mb-3 flex flex-wrap gap-1.5 text-xs">
          {([
            { id: "all", label: "All tiers" },
            { id: "free", label: "Free" },
            { id: "15", label: "Premium R15" },
            { id: "30", label: "Flagship R30" },
          ] as const).map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setTierFilter(f.id)}
              className={`rounded-full border px-2.5 py-1 ${
                tierFilter === f.id
                  ? "border-navy bg-navy text-white"
                  : "border-border bg-white text-navy hover:border-navy"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <ul className="grid max-h-[520px] gap-3 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
          {CV_TEMPLATES.filter((t) => {
            if (tierFilter === "all") return true;
            if (tierFilter === "free") return t.tier === "free";
            const p = t.price ?? 0;
            if (tierFilter === "15") return t.tier === "premium" && p <= 15;
            if (tierFilter === "30") return t.tier === "premium" && p > 15;
            return true;
          }).map((t) => {
            const active = t.id === templateId;
            const priceLabel = t.tier === "premium" ? `R${t.price ?? 0}` : "Free";
            return (
              <li key={t.id}>
                <button
                  onClick={() => setTemplateId(t.id)}
                  className={`block w-full overflow-hidden rounded-md border text-left transition ${
                    active
                      ? "border-navy ring-2 ring-navy/30"
                      : "border-border hover:border-navy"
                  }`}
                >
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-2.5">
                    <MiniPreview template={t} />
                    {t.tier === "premium" && (
                      <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-navy/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                        <Crown className="h-3 w-3" /> {priceLabel}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-navy">{t.name}</span>
                      <span className="shrink-0 text-[11px] font-semibold text-amber-700">
                        {t.hireScore}%
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">
                      {t.bestFor}
                    </p>
                    {t.avoidIf && (
                      <p className="mt-1.5 flex items-start gap-1 text-[10.5px] text-amber-700">
                        <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
                        <span className="line-clamp-2">{t.avoidIf}</span>
                      </p>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Pricing & quality breakdown for selected template */}
        <div className="mt-4 rounded-md border border-navy/15 bg-navy/[0.03] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                Selected · {template.tier === "premium" ? "Premium tier" : "Free tier"}
              </div>
              <div className="mt-0.5 text-base font-semibold text-navy">{template.name}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Best for: {template.bestFor}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Final price
              </div>
              <div className="text-2xl font-bold text-navy">
                {template.tier === "premium" ? `R${template.price ?? 0}` : "Free"}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {template.tier === "premium"
                  ? "Once-off · branded PDF + edits"
                  : `${freeRemaining}/${freeLimit} free AI generations left`}
              </div>
            </div>
          </div>

          <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
            <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
              <dt className="text-muted-foreground">Recruiter hire-score</dt>
              <dd className="font-medium text-navy">{template.hireScore}/100</dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
              <dt className="text-muted-foreground">ATS-safe layout</dt>
              <dd className="font-medium text-navy">Yes</dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
              <dt className="text-muted-foreground">Design complexity</dt>
              <dd className="font-medium text-navy">
                {template.tier === "free"
                  ? "Standard"
                  : (template.price ?? 0) >= 200
                    ? "Flagship / bespoke"
                    : (template.price ?? 0) >= 49
                      ? "Premium"
                      : "Entry premium"}
              </dd>
            </div>
            <div className="flex justify-between gap-2 border-b border-dashed border-navy/10 py-1">
              <dt className="text-muted-foreground">Includes editable PDF</dt>
              <dd className="font-medium text-navy">Yes</dd>
            </div>
          </dl>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {template.tier === "premium" && (
              <a
                href={`/api/public/cv-sample?templateId=${encodeURIComponent(template.id)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-navy/30 bg-white px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy hover:text-white"
              >
                <Download className="h-3.5 w-3.5" />
                Download watermarked sample
              </a>
            )}
            <span className="text-[11px] text-muted-foreground">
              Sample previews are watermarked. Pay to unlock the clean branded PDF.
            </span>
          </div>
        </div>
      </section>



      <section className="rounded-lg border border-border bg-card p-5 space-y-4">
        <h2 className="text-sm font-medium text-navy">3. Tell the AI about the candidate</h2>

        {/* Quick details — easy & accurate fill */}
        <div className="rounded-md border border-navy/15 bg-navy/[0.02] p-3">
          <p className="mb-2 text-xs font-medium text-navy">
            Quick details (optional — fills the background field for you)
          </p>
          {!publicMode && (
            <p className="mb-2 text-[11px] text-muted-foreground">
              One person per account: every CV built here must be in your own
              name. To build a CV for someone else, they must use their own
              account.
            </p>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <input
              value={qd.fullName}
              aria-label="Full name"
              onChange={(e) => setQd({ ...qd, fullName: e.target.value })}
              placeholder="Full name"
              autoComplete="name"
              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
            <input
              value={qd.email}
              aria-label="Email"
              onChange={(e) => setQd({ ...qd, email: e.target.value })}
              placeholder="Email"
              type="email"
              inputMode="email"
              autoComplete="email"
              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
            <input
              value={qd.phone}
              aria-label="Phone number"
              onChange={(e) => setQd({ ...qd, phone: e.target.value })}
              placeholder="Phone (e.g. 071 234 5678)"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
            <input
              value={qd.location}
              aria-label="City, Province"
              onChange={(e) => setQd({ ...qd, location: e.target.value })}
              onBlur={(e) =>
                setQd((p) => ({ ...p, location: normaliseSaLocation(e.target.value) || p.location }))
              }
              placeholder="City (we add the province, e.g. Benoni → Gauteng)"
              autoComplete="address-level2"

              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
            <input
              value={qd.linkedin}
              aria-label="LinkedIn URL"
              onChange={(e) => setQd({ ...qd, linkedin: e.target.value })}
              placeholder="LinkedIn URL (optional)"
              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
            <input
              value={qd.yearsExp}
              aria-label="Years of experience"
              onChange={(e) => setQd({ ...qd, yearsExp: e.target.value })}
              placeholder="Years of experience"
              inputMode="numeric"
              className="rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
          </div>
          <button
            type="button"
            onClick={applyQuickDetails}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-navy bg-background px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
          >
            <Sparkles className="h-3.5 w-3.5" /> Insert into background field
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">
              Target role *
            </span>
            <input
              required
              value={targetRole}
              onChange={(e) => setTargetRole(e.target.value)}
              placeholder="e.g. Compliance Officer, Junior Paralegal, Project Coordinator"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm focus:border-navy focus:outline-none"
            />
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-medium text-foreground/80">Tone</span>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base sm:text-sm"
            >
              <option value="professional">Professional</option>
              <option value="confident">Confident & results-driven</option>
              <option value="warm">Warm & approachable</option>
              <option value="executive">Executive / senior</option>
              <option value="entry">Entry-level / graduate</option>
            </select>
          </label>
        </div>
        <label className="block text-xs">
          <span className="mb-1 block font-medium text-foreground/80">
            Background, experience, education, skills *
          </span>
          <textarea
            value={inputs}
            onChange={(e) => setInputs(e.target.value)}
            rows={10}
            placeholder={EXAMPLE}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setInputs(EXAMPLE)}
            className="mt-1 text-xs text-navy hover:underline"
          >
            Load example
          </button>
        </label>

        <div className="rounded-md border border-dashed border-navy/30 bg-navy/[0.03] p-3 text-[11px] text-foreground/75">
          <p className="flex items-start gap-1.5">
            <Lightbulb className="mt-px h-3.5 w-3.5 shrink-0 text-gold" />
            <span>
              <strong className="text-navy">Power-verb tip:</strong> Start bullets
              with verbs like <em>Led, Implemented, Reduced, Drafted, Negotiated</em>.
              Quantify wherever possible — <em>"40% audit-finding reduction"</em> beats
              <em> "improved audits"</em>. For SA roles, include NQF level, B-BBEE
              status (optional), valid driver's licence, and ID-number availability
              for public-sector vacancies.
            </span>
          </p>
        </div>

        {publicMode && !quotaExhausted && (
          <div className="rounded-md border border-navy/20 bg-navy/[0.03] p-3 text-xs">
            <label className="block">
              <span className="mb-1 flex items-center gap-1.5 font-medium text-navy">
                <ShieldCheck className="h-3.5 w-3.5" />
                Quick check (anti-spam) — what is{" "}
                <strong className="font-semibold">
                  {captcha?.question ?? "…"}
                </strong>
                ?
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  inputMode="numeric"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Answer"
                  className="w-28 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:border-navy focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => void refreshCaptcha()}
                  disabled={captchaLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-[11px] hover:border-navy disabled:opacity-60"
                  title="New question"
                >
                  <RefreshCw
                    className={`h-3 w-3 ${captchaLoading ? "animate-spin" : ""}`}
                  />
                  New
                </button>
              </div>
            </label>
          </div>
        )}

        {!publicMode && entQ.data ? (
          <div
            className={`rounded-md border px-3 py-2 text-xs ${
              authedUnlimited
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : authedNoCredits
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : authedCreditsRemaining <= 1
                    ? "border-orange-300 bg-orange-50 text-orange-900"
                    : "border-emerald-200 bg-emerald-50 text-emerald-900"
            }`}
            aria-live="polite"
          >
            {authedUnlimited ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <strong>✓ Unlimited access.</strong> Your subscription covers
                  unlimited AI CV generations — no credits needed.
                </span>
              </div>
            ) : authedNoCredits ? (
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="mt-0.5">⚠️</span>
                  <div>
                    <p className="font-semibold">You're out of CV credits.</p>
                    <p className="mt-0.5 text-amber-800">
                      Each AI-generated CV uses 1 credit. Top up below at{" "}
                      <strong>R{15} per CV</strong> to keep generating.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="#cv-purchase"
                    className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-amber-700"
                  >
                    Buy CV credits →
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <strong>{authedCreditsRemaining}</strong> CV credit
                  {authedCreditsRemaining === 1 ? "" : "s"} available
                  {entQ.data.freeRemaining > 0
                    ? ` (1 free + ${entQ.data.paidRemaining} paid)`
                    : ` (paid)`}
                  {authedCreditsRemaining === 1 ? (
                    <span className="ml-1 text-orange-800">
                      — last credit. Top up to avoid interruptions.
                    </span>
                  ) : null}
                </span>
                <a
                  href="#cv-purchase"
                  className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold ${
                    authedCreditsRemaining <= 1
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  }`}
                >
                  Top up
                </a>
              </div>
            )}
          </div>
        ) : null}

        {!publicMode && (
          <section className="rounded-lg border border-gold/40 bg-gold/5 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-gold" />
              <h2 className="text-sm font-medium text-navy">
                Tailor this CV to a specific job (optional)
              </h2>
            </div>
            <p className="text-xs text-foreground/70">
              Upload a photo of the job ad and/or paste the description — the AI will rewrite your
              summary, skills and experience bullets to match that role.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => jobFileRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-md border border-input bg-white px-3 py-1.5 text-xs font-medium hover:border-navy"
              >
                <Upload className="h-3.5 w-3.5" />
                {jobPhotoName ? "Replace job ad photo" : "Upload job ad photo"}
              </button>
              <input
                ref={jobFileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setJobPhotoName(e.target.files?.[0]?.name ?? null)}
              />
              {jobPhotoName && (
                <span className="inline-flex items-center gap-1 rounded bg-navy/5 px-2 py-1 text-[11px] text-navy">
                  {jobPhotoName}
                  <button
                    type="button"
                    onClick={() => {
                      setJobPhotoName(null);
                      if (jobFileRef.current) jobFileRef.current.value = "";
                    }}
                    className="text-navy/60 hover:text-navy"
                    aria-label="Remove job photo"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            <textarea
              value={jobText}
              onChange={(e) => setJobText(e.target.value)}
              placeholder="Or paste the job description, requirements, or role brief here…"
              className="min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-navy"
            />
          </section>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => gen.mutate()}
            disabled={
              gen.isPending ||
              !targetRole ||
              !inputs ||
              quotaExhausted ||
              authedNoCredits ||
              (publicMode && (!captcha || !captchaAnswer.trim()))
            }
            title={
              authedNoCredits
                ? "You're out of CV credits — buy a pack below to continue."
                : undefined
            }
            className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {gen.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {cv ? "Regenerate" : "Generate CV"}
          </button>
          <MotivationalLetterGenerator candidateInfo={inputs} />
          {cv && (
            <>
              <button
                onClick={() => pdfDownload.mutate()}
                disabled={pdfDownload.isPending || atsBlocksDownload}
                title={
                  atsBlocksDownload
                    ? `Improve the ATS score to at least ${MIN_ATS_TO_DOWNLOAD} before downloading.`
                    : undefined
                }
                className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-gold-foreground hover:bg-gold/90 disabled:opacity-60"
              >
                {pdfDownload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download PDF{unbranded || publicMode ? " (clean)" : ""}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm hover:border-navy"
                title="Browser print fallback"
              >
                Print
              </button>
            </>
          )}
          {gen.error && (
            <p className="text-xs text-red-600">{(gen.error as Error).message}</p>
          )}
          {pdfDownload.error && (
            <p className="text-xs text-red-600">
              PDF: {(pdfDownload.error as Error).message}
            </p>
          )}
        </div>
      </section>

      {cv && atsReport && (
        <AtsScorePanel
          report={atsReport}
          blocksDownload={atsBlocksDownload}
          minimum={MIN_ATS_TO_DOWNLOAD}
        />
      )}

      {cv && (
        <KeywordChecker
          cv={cv}
          targetRole={targetRole}
          onAddMissing={(skills) => {
            setCv((prev) =>
              prev
                ? {
                    ...prev,
                    skills: Array.from(new Set([...(prev.skills || []), ...skills])),
                  }
                : prev,
            );
            toast.success(
              skills.length === 1
                ? `Added "${skills[0]}" to your skills.`
                : `Added ${skills.length} keywords to your skills.`,
            );
          }}
        />
      )}


      {cv && saSuggestions.length > 0 && (
        <section className="rounded-lg border border-gold/40 bg-gold/5 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-navy">
            <Lightbulb className="h-4 w-4 text-gold" />
            Suggested improvements (South African hiring)
          </h2>
          <ul className="mt-3 space-y-2 text-sm">
            {saSuggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                {s.severity === "warning" ? (
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                ) : (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                )}
                <span>
                  <strong className="text-navy">{s.title}:</strong> {s.body}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Apply changes by editing the inputs and regenerating, or tweak the AI
            output by adding a sentence to the input describing the missing detail.
          </p>
        </section>
      )}

      {cv && (
        <section ref={previewRef} className="rounded-lg border border-border bg-muted/30 p-4 scroll-mt-24">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-navy">
              5. Live preview ({template.name})
            </h2>
            <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
              Updates in real time
            </span>
          </div>

          {/* Inline live-edit so the preview updates as the user types */}
          <div className="mb-4 grid gap-2 rounded-md border border-border bg-card p-3 sm:grid-cols-2">
            <label className="text-xs">
              <span className="mb-1 block font-medium text-foreground/80">Full name</span>
              <input
                value={cv.fullName || ""}
                onChange={(e) => setCv({ ...cv, fullName: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:border-navy focus:outline-none"
              />
            </label>
            <label className="text-xs">
              <span className="mb-1 block font-medium text-foreground/80">Headline</span>
              <input
                value={cv.headline || ""}
                onChange={(e) => setCv({ ...cv, headline: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:border-navy focus:outline-none"
              />
            </label>
            <label className="text-xs sm:col-span-2">
              <span className="mb-1 block font-medium text-foreground/80">Summary</span>
              <textarea
                rows={3}
                value={cv.summary || ""}
                onChange={(e) => setCv({ ...cv, summary: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:border-navy focus:outline-none"
              />
            </label>
            <label className="text-xs sm:col-span-2">
              <span className="mb-1 block font-medium text-foreground/80">Skills (comma separated)</span>
              <input
                value={(cv.skills || []).join(", ")}
                onChange={(e) =>
                  setCv({
                    ...cv,
                    skills: e.target.value.split(/,\s*/).filter(Boolean),
                  })
                }
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:border-navy focus:outline-none"
              />
            </label>
          </div>

          <div className="max-h-[80vh] overflow-y-auto rounded-md bg-white p-2">
            <CVPreview data={cv} template={template} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Tip: edits above update the preview instantly. The{" "}
            <strong>Download PDF</strong> button at the top produces a clean
            ATS-friendly file saved to your <strong>Downloads</strong> folder.
          </p>
        </section>
      )}
    </div>
  );
}

function Stepper({ currentStep }: { currentStep: number }) {
  const steps = [
    { n: 1, label: "Import" },
    { n: 2, label: "Template" },
    { n: 3, label: "Details" },
    { n: 4, label: "Generate" },
  ];
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label="CV builder progress">
      {steps.map((s, i) => {
        const done = currentStep > s.n;
        const active = currentStep === s.n;
        return (
          <li key={s.n} className="flex items-center gap-2">
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold ${
                done
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : active
                    ? "border-navy bg-navy text-navy-foreground"
                    : "border-input bg-background text-muted-foreground"
              }`}
            >
              {done ? "✓" : s.n}
            </span>
            <span
              className={`${active ? "font-semibold text-navy" : done ? "text-emerald-700" : "text-muted-foreground"}`}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span className="mx-1 hidden h-px w-6 bg-border sm:inline-block" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function ShieldDot() {

  return (
    <span
      aria-hidden
      className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600"
    />
  );
}

function AtsScorePanel({
  report,
  blocksDownload,
  minimum,
}: {
  report: AtsReport;
  blocksDownload: boolean;
  minimum: number;
}) {
  const bandColor =
    report.band === "excellent"
      ? "text-emerald-700 bg-emerald-50 border-emerald-300"
      : report.band === "good"
        ? "text-emerald-700 bg-emerald-50 border-emerald-200"
        : report.band === "fair"
          ? "text-amber-700 bg-amber-50 border-amber-300"
          : "text-red-700 bg-red-50 border-red-300";
  const barColor =
    report.score >= 70
      ? "bg-emerald-500"
      : report.score >= 50
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <section
      className={`rounded-lg border p-5 ${blocksDownload ? "border-amber-300 bg-amber-50/50" : "border-border bg-card"}`}
      aria-labelledby="ats-score-heading"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id="ats-score-heading"
          className="flex items-center gap-2 text-sm font-semibold text-navy"
        >
          <ShieldCheck className="h-4 w-4 text-navy" />
          ATS readiness score
        </h2>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${bandColor}`}
        >
          {report.score}/100 ·{" "}
          <span className="capitalize">{report.band}</span>
        </span>
      </div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full transition-all ${barColor}`}
          style={{ width: `${report.score}%` }}
        />
      </div>

      {blocksDownload && (
        <p className="mt-3 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-100/60 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Download unlocks at <strong>{minimum}/100</strong>. Address the
            failed checks below, then click <em>Regenerate</em> to re-score.
          </span>
        </p>
      )}

      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {report.checks.map((c) => (
          <li
            key={c.id}
            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
              c.passed
                ? "border-emerald-200 bg-emerald-50/50 text-emerald-900"
                : "border-amber-200 bg-amber-50/50 text-amber-900"
            }`}
          >
            {c.passed ? (
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
            ) : (
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
            )}
            <span>
              <strong className="block text-[12px] text-navy">{c.label}</strong>
              {c.detail && !c.passed && (
                <span className="text-[11px] text-foreground/75">
                  {c.detail}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}


// === SA-specific CV review heuristics ============================
type SASuggestion = { title: string; body: string; severity: "info" | "warning" };

function computeSASuggestions(cv: CVData | null, rawInputs: string): SASuggestion[] {
  if (!cv) return [];
  const out: SASuggestion[] = [];
  const txt = `${rawInputs || ""} ${cv.summary || ""} ${JSON.stringify(cv.experience || [])} ${(cv.skills || []).join(" ")}`.toLowerCase();

  const bulletText = (cv.experience || [])
    .flatMap((e) => e.bullets || [])
    .join(" ");
  const numberCount = (bulletText.match(/\b\d+%?\b/g) || []).length;
  if (numberCount < 3) {
    out.push({
      title: "Add measurable results",
      body: "Recruiters skim for numbers. Add at least 3 quantified outcomes (%, R-value, headcount, time saved). Example: 'Reduced POPIA audit findings by 40% across 6 branches.'",
      severity: "warning",
    });
  }

  if (!/nqf|nqf level|nqf\s*\d/i.test(txt) && (cv.education || []).length > 0) {
    out.push({
      title: "Note the NQF level",
      body: "SA recruiters and DPSA panels look for NQF levels (e.g. 'BCom Law — NQF Level 7'). Add it next to each qualification.",
      severity: "info",
    });
  }

  if (!cv.languages || cv.languages.length < 2) {
    out.push({
      title: "List SA languages",
      body: "Hiring managers value multilingualism. List spoken languages (English, Afrikaans, isiZulu, Sesotho, Sepedi, Xitsonga, etc.) with proficiency.",
      severity: "info",
    });
  }

  if (!/driver.?s? licen[cs]e|code\s*(08|10|14)|ev\s*1/i.test(txt)) {
    out.push({
      title: "Mention driver's licence",
      body: "Most SA roles ask. State the code (e.g. 'Code 08, own vehicle') in the contact line or 'Additional' section.",
      severity: "info",
    });
  }

  if (!/south african citizen|sa citizen|rsa citizen|permanent resident|work permit/i.test(txt)) {
    out.push({
      title: "Clarify citizenship / work status",
      body: "Public sector and many private roles require this up front: 'South African citizen' or 'Permanent resident' or 'Critical-skills work permit'.",
      severity: "info",
    });
  }

  const sumWords = (cv.summary || "").split(/\s+/).filter(Boolean).length;
  if (sumWords < 40) {
    out.push({
      title: "Beef up the summary",
      body: "The professional summary is short. Aim for 50–80 words covering: years of experience, core domain, top 2 achievements, and the target role.",
      severity: "warning",
    });
  } else if (sumWords > 120) {
    out.push({
      title: "Tighten the summary",
      body: "Summary is long. SA recruiters spend ~7 seconds on first scan — trim to 60–90 words.",
      severity: "info",
    });
  }

  if (/\b\d{13}\b/.test(rawInputs)) {
    out.push({
      title: "Remove full ID number",
      body: "POPIA-wise, never put a full 13-digit ID on a CV that gets emailed around. Only provide it on Z83/HR forms after shortlisting.",
      severity: "warning",
    });
  }

  if (!cv.skills || cv.skills.length < 6) {
    out.push({
      title: "Add more keyword skills",
      body: "ATS systems (PNet, Careers24, SAP SuccessFactors) match on keywords. Aim for 8–14 role-relevant skills mixing hard + soft.",
      severity: "warning",
    });
  }

  return out;
}
